import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getTitleDebtHistory,
    SettlementHttpError,
    type TitleDebtHistory,
} from '../components/pages/settlement/services/settlementApi';

// 404 é o título que a cobrança não conhece; o resto é a cobrança fora do ar, e tentar de novo
// pode resolver. Fora do hook (diretriz: sem função dentro de função).
const failureMessage = (error: unknown): string => {
    if (error instanceof SettlementHttpError && error.status === 404) {
        return 'A cobrança não encontrou este título.';
    }
    if (error instanceof SettlementHttpError && error.status === 403) {
        return 'Sem permissão para consultar a cobrança.';
    }
    return 'Não foi possível consultar a cobrança agora.';
};

/**
 * O dossiê de um título da cobrança, lido ao vivo do settlement (Task 16 / AF-3694). O recovery não
 * guarda cópia: o que a cobrança faz com o título depois do envio (pagamento, protesto, Serasa) só
 * existe lá. Guarda de corrida por geração, como os demais hooks do caso: trocar de título ou
 * recarregar descarta a resposta antiga.
 */
export default function useTitleDebtHistory(titleDebtId: string | null) {
    const [history, setHistory] = useState<TitleDebtHistory | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const generationRef = useRef(0);

    const load = useCallback(async () => {
        const generation = ++generationRef.current;
        setHistory(null);
        setError(null);
        if (!titleDebtId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const result = await getTitleDebtHistory(titleDebtId);
            if (generationRef.current !== generation) return;
            setHistory(result);
        } catch (e) {
            if (generationRef.current !== generation) return;
            setError(failureMessage(e));
        } finally {
            if (generationRef.current === generation) setIsLoading(false);
        }
    }, [titleDebtId]);

    useEffect(() => { load(); }, [load]);

    return { history, isLoading, error, reload: load };
}
